import { AlertTriangle, CheckCircle2, Feather, Loader2, ShieldCheck, Sparkles, X } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';

function DirectionProgress({ job, onCancel }) {
  const pct = Math.round(((job.progress || 0) / Math.max(1, job.total || 1)) * 100);
  return (
    <Card className="border-amber-400/30 bg-amber-500/5 text-neutral-100" data-testid="direction-progress">
      <CardContent className="space-y-3 p-5">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm text-amber-100">
            <Loader2 size={15} className="animate-spin" /> {job.step || 'Writing…'}
          </div>
          <Button size="sm" variant="ghost" onClick={onCancel} className="text-neutral-400" data-testid="direction-cancel-btn"><X size={13} className="mr-1" /> Cancel</Button>
        </div>
        <Progress value={pct} className="h-1.5 bg-neutral-800" />
        <div className="text-[11px] text-neutral-500">Step {Math.min((job.progress || 0) + 1, job.total || 1)} of {job.total || 1} · shots appear as each episode is written</div>
      </CardContent>
    </Card>
  );
}

function OriginalityReport({ report, adaptation }) {
  if (!report || !Object.keys(report).length) return null;
  return (
    <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-4" data-testid="originality-report">
      <div className="mb-2 flex flex-wrap items-center gap-2 text-sm font-semibold text-emerald-200">
        <ShieldCheck size={15} /> Originality report
        {adaptation?.source_title && <span className="text-xs font-normal text-neutral-400">inspired by {adaptation.source_title} → {adaptation.culture_label}</span>}
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <ul className="space-y-1">
          {(report.what_changed || []).map((w) => <li key={w} className="flex gap-2 text-xs text-neutral-300"><CheckCircle2 size={12} className="mt-0.5 shrink-0 text-emerald-300" />{w}</li>)}
        </ul>
        <div className="space-y-2 text-xs">
          {report.cultural_elements?.length > 0 && (
            <div className="flex flex-wrap gap-1">{report.cultural_elements.map((c) => <Badge key={c} className="bg-neutral-800 text-neutral-300">{c}</Badge>)}</div>
          )}
          {(report.twists_added || []).map((t) => <div key={t} className="text-amber-200/90"><Sparkles size={11} className="mr-1 inline" />{t}</div>)}
          {report.plot_kept && <div className="text-neutral-500">Core plot kept: {report.plot_kept}</div>}
        </div>
      </div>
    </div>
  );
}

export function ScreenplayView({ project, onCancel }) {
  const job = project.direction || {};
  const sp = project.screenplay || {};
  const scenes = project.scenes || [];
  return (
    <div className="space-y-4">
      {job.status === 'running' && <DirectionProgress job={job} onCancel={onCancel} />}
      {job.status === 'failed' && (
        <div className="flex items-center gap-2 rounded-lg border border-red-500/30 bg-red-500/5 p-3 text-xs text-red-200" data-testid="direction-error">
          <AlertTriangle size={14} /> Screenplay writing stopped: {job.error || 'unknown error'} — adjust the brief and try again.
        </div>
      )}
      {sp.title && (
        <Card className="border-neutral-800 bg-neutral-900/40 text-neutral-100" data-testid="screenplay-view">
          <CardContent className="space-y-4 p-5">
            <div>
              <div className="mb-1 flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-amber-300/80"><Feather size={12} /> Screenplay</div>
              <h2 className="text-2xl font-semibold tracking-tight text-neutral-50" data-testid="screenplay-title">{sp.title}</h2>
              <p className="mt-1 text-sm italic text-neutral-300">{sp.logline}</p>
              <p className="mt-2 max-w-3xl text-xs leading-relaxed text-neutral-400">{sp.synopsis}</p>
              {sp.themes?.length > 0 && <div className="mt-2 flex flex-wrap gap-1">{sp.themes.map((t) => <Badge key={t} className="bg-neutral-800 text-neutral-300">{t}</Badge>)}</div>}
            </div>
            <OriginalityReport report={sp.originality_report} adaptation={project.adaptation} />
            <Accordion type="multiple" defaultValue={['ep-1']} className="rounded-lg border border-neutral-800">
              {(sp.episodes || []).map((ep) => {
                const epShots = scenes.filter((s) => (s.episode || 1) === ep.number);
                return (
                  <AccordionItem key={ep.number} value={`ep-${ep.number}`} className="border-neutral-800 px-4" data-testid={`screenplay-episode-${ep.number}`}>
                    <AccordionTrigger className="text-sm hover:no-underline">
                      <span className="flex items-center gap-3 text-left">
                        <span className="font-mono text-xs text-amber-300">EP{String(ep.number).padStart(2, '0')}</span>
                        {ep.title}
                        <span className="text-[11px] font-normal text-neutral-500">{epShots.length} shots · {epShots.reduce((a, s) => a + (s.duration_seconds || 0), 0)}s</span>
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="space-y-2 text-xs text-neutral-400">
                      <p>{ep.synopsis}</p>
                      {ep.cliffhanger && <p className="text-amber-200/80">Cliffhanger: {ep.cliffhanger}</p>}
                      <ol className="space-y-1 border-l border-neutral-800 pl-3">
                        {epShots.map((s, i) => (
                          <li key={s.id}><span className="text-neutral-200">{i + 1}. {s.title || 'Shot'}</span> — {s.description}
                            {s.cinematography?.camera_movement && <span className="text-amber-300/70"> · {s.cinematography.camera_movement}</span>}</li>
                        ))}
                      </ol>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
